import dynamic, { LoaderComponent } from 'next/dynamic';

import { IM30ProductCarouselModule, IModules, IOptionalSkeletonModule, IRequiredSkeletonModule } from '~/lib/data-contract';
import { M10HeroProps } from '~/templates/blocks/components/M10Hero/M10Hero';
import { M11HeroProps } from '~/templates/blocks/components/M11Hero/M11Hero';
import type { M20CategoriesProps } from '~/templates/blocks/components/M20Categories/M20Categories';
import type { M21CategoriesProps } from '~/templates/blocks/components/M21Categories/M21Categories';
import { M30CarouselPlaceholder } from '~/templates/blocks/components/M30ProductCarousel/M30CarouselPlaceholder';

import type { M1000CookieInformationProps } from '../M1000CookieInformation/M1000CookieInformation';
import type { M100RichTextProps } from '../M100RichText/M100RichText';
import type { M110NewsletterProps } from '../M110Newsletter/M110Newsletter';
import type { M140ContentSpotProps } from '../M140ContentSpot/M140ContentSpot';
import type { M170ProductFilterProps } from '../M170ProductFilter/models/M170ProductFilter';
import type { M180BrandOverviewProps } from '../M180BrandOverview';
import type { M190BrandOverviewStoriesProps } from '../M190BrandOverviewStories/M190BrandOverviewStories';
import type { M210PreferenceCenterProps } from '../M210PreferenceCenter/M210PreferenceCenter';
import type { M25CategorySpotProps } from '../M25CategorySpot/M25CategorySpot';
import type { M50ShopTheLookProps } from '../M50ShopTheLook';
import type { M550ProductSpecsProps } from '../M550ProductSpecs';
import type { M560TabListProps } from '../M560TabList/M560TabList';
import type { M60UspsProps } from '../M60Usps/M60Usps';
import type { M70MediaProps } from '../M70Media/M70Media';
import type { M71MediaBundleProps } from '../M71MediaBundle/M71MediaBundle';
import type { M75MediaTextProps } from '../M75MediaText/M75MediaText';
import type { M80MediaTextBundleProps } from '../M80MediaTextBundle';
import { PersonalizedContent } from '../PersonalizedContent/components/PersonalizedContent';

const M75MediaText = dynamic(() => import('../M75MediaText/M75MediaText').then((module) => module.M75MediaText) as LoaderComponent<M75MediaTextProps>);

const M50ShopTheLook = dynamic(() => import('../M50ShopTheLook').then((module) => module.M50ShopTheLook) as LoaderComponent<M50ShopTheLookProps>);

const M180BrandOverview = dynamic(() => import('../M180BrandOverview').then((module) => module.M180BrandOverview) as LoaderComponent<M180BrandOverviewProps>);

const M30ProductCarousel = dynamic(
	() => import('../M30ProductCarousel/M30ProductCarousel').then((module) => module.M30ProductCarousel) as LoaderComponent<IM30ProductCarouselModule>,
);

const M170ProductFilter = dynamic(
	() => import('../M170ProductFilter/M170ProductFilter').then((module) => module.M170ProductFilter) as LoaderComponent<M170ProductFilterProps>,
);

const M70Media = dynamic(() => import('../M70Media/M70Media').then((module) => module.M70Media) as LoaderComponent<M70MediaProps>);

const M80MediaTextBundle = dynamic(
	() => import('../M80MediaTextBundle').then((module) => module.M80MediaTextBundle) as LoaderComponent<M80MediaTextBundleProps>,
);

const M550ProductSpecs = dynamic(() => import('../M550ProductSpecs').then((module) => module.M550ProductSpecs) as LoaderComponent<M550ProductSpecsProps>);

const M560TabList = dynamic(() => import('../M560TabList/M560TabList').then((module) => module.M560TabList) as LoaderComponent<M560TabListProps>);

const M140ContentSpot = dynamic(
	() => import('../M140ContentSpot/M140ContentSpot').then((module) => module.M140ContentSpot) as LoaderComponent<M140ContentSpotProps>,
);

const M71MediaBundle = dynamic(
	() => import('../M71MediaBundle/M71MediaBundle').then((module) => module.M71MediaBundle) as LoaderComponent<M71MediaBundleProps>,
	{ ssr: false },
);

const M10Hero = dynamic(() => import('~/templates/blocks/components/M10Hero/M10Hero').then((module) => module.M10Hero) as LoaderComponent<M10HeroProps>);

const M11Hero = dynamic(() => import('~/templates/blocks/components/M11Hero/M11Hero').then((module) => module.M11Hero) as LoaderComponent<M11HeroProps>);

const M60Usps = dynamic(() => import('../M60Usps/M60Usps').then((module) => module.M60Usps) as LoaderComponent<M60UspsProps>);

const M25CategorySpot = dynamic(
	() => import('../M25CategorySpot/M25CategorySpot').then((module) => module.M25CategorySpot) as LoaderComponent<M25CategorySpotProps>,
);

const M100RichText = dynamic(() => import('../M100RichText/M100RichText').then((module) => module.M100RichText) as LoaderComponent<M100RichTextProps>);

const M190BrandOverviewStories = dynamic(
	() =>
		import('../M190BrandOverviewStories/M190BrandOverviewStories').then(
			(module) => module.M190BrandOverviewStories,
		) as LoaderComponent<M190BrandOverviewStoriesProps>,
);

const M20Categories = dynamic(
	() => import('~/templates/blocks/components/M20Categories/M20Categories').then((module) => module.M20Categories) as LoaderComponent<M20CategoriesProps>,
);

const M21Categories = dynamic(
	() => import('~/templates/blocks/components/M21Categories/M21Categories').then((module) => module.M21Categories) as LoaderComponent<M21CategoriesProps>,
);

const M110Newsletter = dynamic(
	() => import('../M110Newsletter/M110Newsletter').then((module) => module.M110Newsletter) as LoaderComponent<M110NewsletterProps>,
);

const M1000CookieInformation = dynamic(
	() =>
		import('../M1000CookieInformation/M1000CookieInformation').then((module) => module.M1000CookieInformation) as LoaderComponent<M1000CookieInformationProps>,
	{ ssr: false },
);

const M210PreferenceCenter = dynamic(
	() => import('../M210PreferenceCenter/M210PreferenceCenter').then((module) => module.M210PreferenceCenter) as LoaderComponent<M210PreferenceCenterProps>,
	{
		ssr: false,
	},
);

const M101CareerPortal = dynamic(() => import('../M101CareerPortal/M101CareerPortal').then((module) => module.M101CareerPortal), {
	ssr: false,
});

type PageElement = IModules | IOptionalSkeletonModule | IRequiredSkeletonModule;

type DynamicBlockProps = PageElement & {
	isSpecialProductDetail?: boolean;
};

export const DynamicBlock = ({ isSpecialProductDetail, ...element }: DynamicBlockProps) => {
	switch (element.type) {
		case 'm10HeroModule':
			return <M10Hero {...element} />;

		case 'm11HeroModule':
			return <M11Hero {...element} />;

		case 'm30ProductCarouselModule':
			return <M30ProductCarousel {...element} />;

		case 'm30ProductCarouselSkeletonModule': // TODO: Render m30 with loading icons
			return (
				<PersonalizedContent
					id={element.id}
					placeholder={<M30CarouselPlaceholder />}
				/>
			);
		case 'optionalSkeletonModule':
		case 'requiredSkeletonModule':
			return <PersonalizedContent id={element.id} />;

		case 'm75MediaAndTextModule':
			return <M75MediaText {...element} />;

		case 'm50ShopTheLookModule':
			return <M50ShopTheLook {...element} />;

		case 'm180BrandOverviewModule':
			return <M180BrandOverview {...element} />;

		case 'm170ProductFilterModule':
			return <M170ProductFilter initialData={element} />;

		case 'm70MediaModule':
			return <M70Media {...element} />;

		case 'm80MediaAndTextBundleModule':
			return <M80MediaTextBundle {...element} />;

		case 'm140ContentSpotModule':
			return <M140ContentSpot {...element} />;

		case 'm71MediaBundleModule':
			return <M71MediaBundle {...element} />;

		case 'm550ProductSpecsModule':
			return <M550ProductSpecs {...element} />;

		case 'm560TabListModule':
			return (
				<M560TabList
					isSpecialProductDetail={isSpecialProductDetail}
					{...element}
				/>
			);

		case 'm60UspModule':
			return <M60Usps {...element} />;

		case 'm25CategorySpotModule':
			return <M25CategorySpot {...element} />;

		case 'm100RichTextModule':
			return <M100RichText {...element} />;

		case 'm1000CookieInformationModule':
			return <M1000CookieInformation {...element} />;

		case 'm190BrandOverviewModule':
			return <M190BrandOverviewStories {...element} />;

		case 'm20CategoriesModule':
			if (element.categoryImages) {
				return <M21Categories {...element} />;
			}

			return <M20Categories {...element} />;

		case 'm110NewsletterSignupSkeletonModule':
			return <M110Newsletter {...element} />;

		case 'm210PreferenceCenterSkeletonModule':
			return <M210PreferenceCenter {...element} />;

		case 'm101CareerPortalModule':
			return <M101CareerPortal {...element} />;

		default:
			console.warn('No mapping for module', element.type);
			return null;
	}
};
